import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';
import { MapService } from '../google-map/map.service';
import { CommonService, DocumentsService } from '../../services/common';
import { ToastrService } from 'ngx-toastr';
import { IColumnDef } from '../common-table/common-table.model';

@Component({
    selector: 'app-kml-upload',
    templateUrl: './kml-upload.component.html',
    styleUrls: ['./kml-upload.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class KmlUploadComponent implements OnChanges {
    @Input() visible: boolean = false;
    @Input() mapData: any; // Contains kmlFor and geographical hierarchy data
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() layoutVersionChanged = new EventEmitter<any>(); // Emits event to refresh KML URLs after successful submission

    @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

    // Tab management
    activeTab: 'upload' | 'manage' = 'upload';

    // Upload form properties
    selectedFile: File | null = null;
    kmlType: string = '';
    versionName: string = '';
    selectedVersion: string = ''; // Track selected version from dropdown or 'ADD_NEW' for new version
    isLoading: boolean = false;
    isDragOver: boolean = false;
    projectId: number | undefined;
    layoutVersionsOptions: any[] = []; // Cached options to prevent change detection issues

    // File management properties
    kmlFilesList: any[] = [];
    isLoadingFilesList: boolean = false;
    deletingPath: string | null = null; // Track which file is being deleted
    kmlFilesColumnDefs: IColumnDef[] = [];

    // KML Type options (matching toggle values in plain-mapview)
    kmlTypeOptions = [
        { label: 'Barren', value: 'Barren' },
        { label: 'Stitching', value: 'Stitching' },
        { label: 'Zone', value: 'VillageMasterPlan' },
        { label: 'Layout', value: 'PlainLayout' },
        { label: 'Artist', value: 'Visualise' }
    ];

    constructor(
        private mapService: MapService,
        private commonService: CommonService,
        private documentsService: DocumentsService,
        private toastr: ToastrService
    ) { }

    /**
     * Initialize KML files table column definitions
     */
    initializeFileTableColumns() {
        this.kmlFilesColumnDefs = [
            {
                field: 'fileExtension',
                header: '',
                columnType: 'fileIcon',
                tooltipField: 'tooltip' // Show full URL on hover
            },
            {
                field: 'displayName',
                header: 'File/Version',
                columnType: 'text'
            },
            {
                field: 'fileType',
                header: 'File Type',
                columnType: 'text'
            },
            {
                field: 'actions',
                header: 'Action',
                columnType: 'icon',
                config: {
                    icons: [
                        {
                            name: 'delete',
                            icon: 'fa-trash-alt',
                            tooltip: 'Delete file',
                            isImage: false
                        }
                    ]
                }
            }
        ];
    }

    /**
     * Detect when visible or mapData changes
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes['visible'] && changes['visible'].currentValue) {
            this.initializeFileTableColumns();
            // Read projectId once when modal opens
            this.projectId = this.getProjectId();
        }

        // When mapData changes, rebuild the layout versions options
        if (changes['mapData']) {
            this.buildLayoutVersionsOptions();

            // Check if the newly created version is now in the layoutVersions array
            if (this.visible && this.versionName) {
                if (this.hasLayoutVersions && this.mapData.layoutVersions.includes(this.versionName)) {
                    // Auto-select the newly created version in the dropdown
                    this.selectedVersion = this.versionName;
                }
            }
        }
    }

    /**
     * Build cached layout versions options array
     */
    private buildLayoutVersionsOptions(): void {
        const versions = this.mapData?.layoutVersions || [];
        this.layoutVersionsOptions = [
            ...versions.map((v: any) => ({ label: v, value: v })),
            { label: 'Add New Version', value: 'ADD_NEW' }
        ];
    }

    /**
     * Determine if this is for Inventory/Sales (show kmlType dropdown)
     */
    get showKmlTypeInput(): boolean {
        return this.mapData?.kmlFor?.toLowerCase() === 'acquisition' || this.mapData?.kmlFor?.toLowerCase() === 'inventory';
    }

    /**
     * Check if layoutVersions exist in mapData
     */
    get hasLayoutVersions(): boolean {
        return Array.isArray(this.mapData?.layoutVersions) && this.mapData.layoutVersions.length > 0;
    }

    /**
     * Check if user should be able to input new version name
     */
    get showVersionInput(): boolean {
        // Show input if:
        // 1. No versions exist (show by default), OR
        // 2. User selected "Add New Version" from dropdown
        return !this.hasLayoutVersions || this.selectedVersion === 'ADD_NEW';
    }

    /**
     * Handle dialog visibility change
     */
    onHideDialog() {
        this.visible = false;
        this.visibleChange.emit(false);
        this.resetForm();
    }

    /**
     * Trigger file input click
     */
    triggerFileInput() {
        if (this.fileInput) {
            this.fileInput.nativeElement.click();
        }
    }

    /**
     * Handle file selection
     */
    onFileSelected(event: any) {
        const file: File = event.target.files[0];
        if (!file) {
            return;
        }

        // Validate file type
        const validExtensions = ['kml', 'kmz', 'png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!validExtensions.includes(fileExtension || '')) {
            this.toastr.error('Please select a valid KML, KMZ, or PNG file');
            return;
        }

        this.selectedFile = file;
    }

    /**
     * Handle drag over event
     */
    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    /**
     * Handle drag leave event
     */
    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    /**
     * Handle drop event
     */
    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];

        // Validate file type
        const validExtensions = ['kml', 'kmz', 'png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!validExtensions.includes(fileExtension || '')) {
            this.toastr.error('Please select a valid KML, KMZ, or PNG file');
            return;
        }

        this.selectedFile = file;
    }

    /**
     * Handle version selection change
     */
    onVersionChange() {
        // Only clear versionName when user selects "Add New Version"
        if (this.selectedVersion === 'ADD_NEW') {
            this.versionName = '';
        }
        // When selecting an existing version, selectedVersion holds the value
        // No need to clear versionName as it won't be used
    }

    /**
     * Reset form
     */
    resetForm() {
        this.selectedFile = null;
        this.kmlType = '';
        this.versionName = '';
        this.selectedVersion = '';
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    /**
     * Get project ID from CommonService or mapData
     * For inventory uploads, projectId is primarily read from CommonService (selectedProject)
     */
    private getProjectId(): number | undefined {
        if (this.mapData?.projectId) {
            return this.mapData.projectId;
        }

        return undefined;
    }

    /**
     * Submit KML upload
     */
    async submitUpload() {
        // Validation: KML File selection
        if (!this.selectedFile) {
            this.toastr.error('Please select a KML file');
            return;
        }

        // Validation: KML Type (required for inventory/acquisition)
        if (this.showKmlTypeInput && !this.kmlType) {
            this.toastr.error('Please select KML Type');
            return;
        }

        // Validation: kmlFor is available
        if (!this.mapData?.kmlFor) {
            this.toastr.error('KML upload type is not defined');
            return;
        }

        // Validation: projectId (required for inventory uploads) - using pre-loaded value unless kmlLevel is exploration
        if (this.mapData?.kmlFor?.toLowerCase() === 'inventory' && this.mapData?.kmlLevel !== 'exploration' && !this.projectId) {
            this.toastr.error('Project ID is missing. Please ensure you are logged in to a project.');
            return;
        }

        // All validations passed, prepare for upload
        this.isLoading = true;

        // Determine processKhasras based on kmlFor
        const processKhasras = this.mapData?.kmlFor?.toLowerCase() === 'inventory' ? false : false;

        // Determine the version to use in payload
        let versionToSend: string | undefined = undefined;
        if (this.selectedVersion && this.selectedVersion !== 'ADD_NEW') {
            // User selected an existing version from dropdown
            versionToSend = this.selectedVersion;
        } else if (this.versionName) {
            // User typed a new version name
            versionToSend = this.versionName;
        }

        let landArea: number | undefined = undefined;
        let landAreaUnit: number | undefined = undefined;
        // Calculate area if kmlType is stitching and file is a .kml
        if (this.kmlType?.toLowerCase() === 'stitching' && this.selectedFile.name.toLowerCase().endsWith('.kml')) {
            try {
                const areaResult = await this.mapService.calculateKmlArea(this.selectedFile);
                landArea = areaResult.area;
                landAreaUnit = areaResult.unitMeasurementId;
            } catch (err) {
                console.error("Error calculating Stitching area", err);
                // Non-blocking error, allow upload to proceed but without area
            }
        }

        // Call map service to save KML using pre-loaded projectId
        this.mapService.saveKml(
            this.selectedFile,
            this.mapData.kmlFor,
            this.mapData?.countryId,
            this.mapData?.stateId,
            this.mapData?.districtId,
            this.mapData?.tehsilId,
            this.mapData?.villageId,
            this.projectId,
            this.mapData?.landKhasraId,
            this.kmlType || undefined,
            versionToSend,
            processKhasras,
            this.mapData?.explorationId,
            landArea,
            landAreaUnit
        ).subscribe(
            (response) => {
                this.isLoading = false;
                this.toastr.success('KML file uploaded successfully');

                // Emit event to trigger KML URL refresh with version details
                this.layoutVersionChanged.emit({
                    versionName: versionToSend, // Send the version that was actually used
                    kmlFor: this.mapData?.kmlFor,
                    kmlType: this.kmlType
                });

                // If stitching KML, update local storage and observables with area data
                if (this.kmlType?.toLowerCase() === 'stitching' && landArea !== undefined) {
                    this.updateAreaInSession(landArea, landAreaUnit);
                }

                // Close the modal
                this.visible = false;
                this.visibleChange.emit(false);
                this.resetForm();
            },
            (error) => {
                this.isLoading = false;
                this.toastr.error('Error uploading KML file. Please try again.');
            }
        );
    }

    /**
     * Switch between tabs
     */
    switchTab(tab: 'upload' | 'manage') {
        this.activeTab = tab;
        if (tab === 'manage') {
            this.loadKmlFilesList();
        }
    }

    /**
     * Load KML files list from mapData
     * Maps mapData.mapUrl (files) and mapData.layoutVersions (versions) into display items
     */
    loadKmlFilesList() {
        this.isLoadingFilesList = true;

        try {
            const fileUrls = this.mapData?.mapUrl || [];
            const versions = this.mapData?.layoutVersions || [];
            this.kmlFilesList = [];

            // Map file URLs to display items
            // File name extracted from URL (e.g., "Stitching", "Barren", "Layout", "Zoning")
            fileUrls.forEach((fileUrl: any, index: number) => {
                const filePath = typeof fileUrl === 'string' ? this.documentsService.getKMLFilePathFromUrl(fileUrl) : fileUrl.path || fileUrl;
                const fileName = filePath.lastIndexOf('/') > -1 ? filePath.substring(filePath.lastIndexOf('/') + 1) : filePath;
                const fileType = this.extractFileType(filePath);
                const extension = this.extractFileExtension(filePath);
                const tooltipText = filePath && typeof filePath === 'string' && filePath.trim() ? filePath : 'KML File';

                const item = {
                    path: filePath,
                    displayName: fileName,
                    itemType: fileType,
                    isVersionFolder: false,  // Regular file, not a version folder
                    fileExtension: extension,
                    fileType: extension.toUpperCase(),
                    tooltip: tooltipText
                };

                this.kmlFilesList.push(item);
            });

            // Map version names to display items
            versions.forEach((version: any, index: number) => {
                const versionName = typeof version === 'string'
                    ? version
                    : version.versionName || version.name || version;
                const versionPath = typeof version === 'string'
                    ? version
                    : version.path || version.name || version;
                const extension = this.extractFileExtension(versionPath);
                const tooltipText = versionPath && typeof versionPath === 'string' && versionPath.trim() ? versionPath : 'KML Version';

                const item = {
                    path: versionPath,
                    displayName: versionName,
                    itemType: 'kml',
                    isVersionFolder: true,  // This is a version folder
                    kmlType: 'Version',
                    fileExtension: extension,
                    fileType: extension.toUpperCase(),
                    tooltip: tooltipText
                };

                this.kmlFilesList.push(item);
            });

            this.isLoadingFilesList = false;
        } catch (error) {
            this.isLoadingFilesList = false;
            this.toastr.error('Failed to load KML files');
        }
    }

    /**
     * Handle table row action (icon click)
     */
    onKmlFileTableAction(event: any) {
        if (event?.action === 'delete') {
            this.deleteKmlFile(event.row);
        }
    }

    /**
     * Construct full path for KML file/folder deletion
     * Handles both regular files and version folders
     * @param file Item to delete (contains path, displayName, isVersionFolder flag)
     * @returns Full path for the backend API
     */
    private buildFullKmlPath(file: any): string {
        // If path already looks like a full path (contains multiple /), use it as-is
        if (file.path && file.path.includes('/')) {
            return file.path;
        }

        // For version folders: path is just the version name, needs full construction
        if (file.isVersionFolder) {
            // Construct: KML Files/Dashboard/{kmlFor}/Project/{projectId}/{versionName}
            const kmlFor = this.mapData?.kmlFor || 'Inventory';
            if (!this.projectId) {
                throw new Error('Project ID is missing for version folder deletion');
            }
            return `KML Files/Dashboard/${kmlFor}/Project/${this.projectId}/${file.path}`;
        }

        // For regular files, path should already be complete (from mapUrl)
        return file.path;
    }

    /**
     * Delete KML file by path
     */
    deleteKmlFile(file: any) {
        const confirmDelete = confirm(`Are you sure you want to delete "${file.displayName}"?`);
        if (!confirmDelete) {
            return;
        }

        this.deletingPath = file.path;

        try {
            // Build the full path (handles both files and version folders)
            const fullPath = this.buildFullKmlPath(file);

            // Call deleteFileOrFolderByPath from DocumentsService
            this.documentsService.deleteFileOrFolderByPath(fullPath).subscribe(
                (response: any) => {
                    this.deletingPath = null;
                    this.toastr.success('File deleted successfully');

                    // If stitching KML deleted, reset area in local storage and observables
                    if (file.itemType?.toLowerCase() === 'stitching') {
                        this.updateAreaInSession(0, undefined);
                    }

                    // Immediately remove the deleted item from the list without waiting for mapData update
                    this.kmlFilesList = this.kmlFilesList.filter(item => item.path !== file.path);

                    // Emit event to refresh KML display
                    this.layoutVersionChanged.emit({
                        action: 'delete',
                        path: file.path,
                        isVersionFolder: file.isVersionFolder
                    });
                },
                (error: any) => {
                    this.deletingPath = null;
                    this.toastr.error('Error deleting file. Please try again.');
                    console.error('Delete error:', error);
                }
            );
        } catch (error: any) {
            this.deletingPath = null;
            this.toastr.error(error.message || 'Error preparing file for deletion');
            console.error('Path construction error:', error);
        }
    }

    /**
     * Extract file name from path
     */
    private extractFileName(path: string): string {
        if (!path) return 'Unknown';
        return path.split('/').pop() || path;
    }

    /**
     * Extract file extension from URL/path
     * Returns just the extension (e.g., 'kml', 'kmz', 'png')
     */
    private extractFileExtension(url: string): string {
        if (!url) return 'file';
        const extension = url.split('.').pop()?.toLowerCase() || 'file';
        return extension;
    }

    /**
     * Extract file type/name from URL by checking for KML type keywords
     * Looks for: Barren, Stitching, Layout, Zoning (VillageMasterPlan), Visualise/Artist
     */
    private extractFileType(url: string): string {
        if (!url) return 'File';

        const urlLower = url.toLowerCase();

        // Check for specific KML type keywords in the URL
        if (urlLower.includes('barren')) return 'Barren';
        if (urlLower.includes('stitching')) return 'Stitching';
        if (urlLower.includes('layout') || urlLower.includes('plainlayout')) return 'Layout';
        if (urlLower.includes('zoning') || urlLower.includes('villagemaster') || urlLower.includes('vmp')) return 'Zoning';
        if (urlLower.includes('visualise') || urlLower.includes('artist')) return 'Artist';

        // Default: extract file extension if no keyword matches
        const ext = url.split('.').pop()?.toUpperCase() || 'File';
        return ext;
    }

    /**
     * Format file size for display
     */
    private formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }

    /**
     * Updates the landArea and landAreaUnit in local storage and active selected objects
     */
    private updateAreaInSession(landArea: number | undefined, landAreaUnit: number | undefined) {
        const setAreaAndUnit = (obj: any) => {
            if (obj) {
                obj.landArea = landArea;
                obj.landAreaUnit = landAreaUnit;
            }
        };

        if (this.projectId && this.projectId > 0) {
            // Update userProjects array
            const userProjectsStr = this.commonService.getValueFromLocalStorage('userProjects');
            if (userProjectsStr) {
                try {
                    const userProjects = JSON.parse(userProjectsStr);
                    const proj = userProjects.find((p: any) => p.projectId == this.projectId || p.id == this.projectId);
                    if (proj) {
                        setAreaAndUnit(proj);
                        this.commonService.setLocalStorageValue('userProjects', JSON.stringify(userProjects));
                    }
                } catch (e) { }
            }
            // Update selectedProject
            const selectedProjectStr = this.commonService.getValueFromLocalStorage('selectedProject');
            if (selectedProjectStr) {
                try {
                    const selectedProject = JSON.parse(selectedProjectStr);
                    if (selectedProject?.projectId == this.projectId || selectedProject?.id == this.projectId) {
                        setAreaAndUnit(selectedProject);
                        this.commonService.setLocalStorageValue('selectedProject', JSON.stringify(selectedProject));
                        this.commonService.updateSelectedProjects(selectedProject);
                    }
                } catch (e) { }
            }
        } else if (this.mapData?.explorationId && this.mapData.explorationId > 0) {
            // Update userExplorations array
            const userExplorationsStr = this.commonService.getValueFromLocalStorage('userExplorations');
            if (userExplorationsStr) {
                try {
                    const userExplorations = JSON.parse(userExplorationsStr);
                    const exp = userExplorations.find((e: any) => e.explorationId == this.mapData.explorationId || e.id == this.mapData.explorationId);
                    if (exp) {
                        setAreaAndUnit(exp);
                        this.commonService.setLocalStorageValue('userExplorations', JSON.stringify(userExplorations));
                    }
                } catch (e) { }
            }
            // Update selectedExploration
            const selectedExplorationStr = this.commonService.getValueFromLocalStorage('selectedExploration');
            if (selectedExplorationStr) {
                try {
                    const selectedExploration = JSON.parse(selectedExplorationStr);
                    if (selectedExploration?.explorationId == this.mapData.explorationId || selectedExploration?.id == this.mapData.explorationId) {
                        setAreaAndUnit(selectedExploration);
                        this.commonService.setLocalStorageValue('selectedExploration', JSON.stringify(selectedExploration));
                        this.commonService.updateSelectedExplorations(selectedExploration);
                    }
                } catch (e) { }
            }
        }
    }
}
